import { EXPRESSION_TYPES, StepDefinitionId, Temporal, toLegacyDate, toTemporalInstant } from '@willow/types-iso';

import { getMostRecentQrpcEntrySince } from '../../../loan/qrpc';
import { getDaysPastDueAsOfDate as getDaysPastDueAsOfDateImpl } from '../../../utils/payments';
import { functionFrom, FunctionLibrary, getFunctionSignature } from '../FunctionDefinition';

const loanHasPaymentDeferral = functionFrom(
  'loanHasPaymentDeferral',
  [] as const,
  EXPRESSION_TYPES.boolean,
  (_ctx) => false, // TODO
  'Indicates whether the loan currently has a payment deferral in effect (stub implementation).',
);

const loanHasRepaymentPlan = functionFrom(
  'loanHasRepaymentPlan',
  [] as const,
  EXPRESSION_TYPES.boolean,
  (ctx) => ctx.getLoanSnapshot().configuration.workoutPlans.length > 1, // TODO
  'Returns true if the loan snapshot shows an active repayment plan (stub logic).',
);

const loanHasForebearancePlan = functionFrom(
  'loanHasForebearancePlan',
  [] as const,
  EXPRESSION_TYPES.boolean,
  (_ctx) => false, // TODO
  'Returns true if the loan has an active forbearance plan (stub implementation).',
);

const loanHasNoPastDueStatements = functionFrom(
  'loanHasNoPastDueStatements',
  [] as const,
  EXPRESSION_TYPES.boolean,
  (_ctx) => false, // TODO
  'Returns true when no past-due billing statements exist for the loan (stub implementation).',
);

const loanHasQRPCObtainedSince = functionFrom(
  'loanHasQRPCObtainedSince',
  [{ name: 'sinceDate', type: EXPRESSION_TYPES.zonedDateTime }] as const,
  EXPRESSION_TYPES.boolean,
  (ctx, sinceZdt) => {
    const mostRecentQrpcEntry = getMostRecentQrpcEntrySince(
      ctx.getLoanSnapshot().configuration.qrpc ?? [],
      sinceZdt.toPlainDate(),
    );
    return mostRecentQrpcEntry?.qrpcObtainedFlag ?? false;
  },
  "Returns true if the loan's most recent QRPC entry since the given date time indicates that QRPC was obtained.",
);

/**
 * Returns the next QRPC collection due date:
 * - If the loan has a QRPC promise to pay expiration date, retry/due on the promise to pay expiration date
 * - If the loan does not have a QRPC promise to pay expiration date, retry/due 6 days from the last attempt
 */
const getNextQrpcDueDateTime = functionFrom(
  'getNextQrpcDueDateTime',
  [
    { name: 'since', type: EXPRESSION_TYPES.zonedDateTime },
    { name: 'stepDefinitionId', type: EXPRESSION_TYPES.string },
  ] as const,
  EXPRESSION_TYPES.zonedDateTime,
  (ctx, sinceZdt, stepDefinitionId) => {
    const mostRecentQrpcEntry = getMostRecentQrpcEntrySince(
      ctx.getLoanSnapshot().configuration.qrpc ?? [],
      sinceZdt.toPlainDate(),
    );
    if (mostRecentQrpcEntry?.promiseToPayExpirationDate != null) {
      return toTemporalInstant(mostRecentQrpcEntry.promiseToPayExpirationDate).toZonedDateTimeISO(ctx.workflowTimeZone);
    }
    const lastStepAttempt = [...(ctx.getStepByDefinitionId(stepDefinitionId as StepDefinitionId)?.statusHistory ?? [])]
      .reverse()
      .find((status) => status.status === 'active');
    if (lastStepAttempt == null) {
      // this should never happen - steps should always have at least one history entry
      throw new Error(`Step with definitionId ${stepDefinitionId} has no activation history`);
    }
    return lastStepAttempt.timestamp.add(Temporal.Duration.from({ days: 6 }));
  },
  'Returns the zoned date-time of when the next QRPC collection attempt should be made.',
);

const loanHasAppraisalWithId = functionFrom(
  'loanHasAppraisalWithId',
  [{ name: 'appraisalId', type: EXPRESSION_TYPES.string }] as const,
  EXPRESSION_TYPES.boolean,
  (ctx, appraisalId) => ctx.getLoanSnapshot().configuration.appraisals.some((a) => a.id === appraisalId),
  'Returns true if the loan snapshot has an appraisal with the given ID. Pair with `preallocatedEntityRef` for workflow steps that record an appraisal.',
);

const loanHasInspectionWithId = functionFrom(
  'loanHasInspectionWithId',
  [{ name: 'inspectionId', type: EXPRESSION_TYPES.string }] as const,
  EXPRESSION_TYPES.boolean,
  (ctx, inspectionId) => ctx.getLoanSnapshot().configuration.inspections.some((i) => i.id === inspectionId),
  'Returns true if the loan snapshot has an inspection with the given ID. Pair with `preallocatedEntityRef` for workflow steps that record an inspection.',
);

const getNumDaysPastDueAsOfDate = functionFrom(
  'getNumDaysPastDueAsOfDate',
  [{ name: 'targetDate', type: EXPRESSION_TYPES.zonedDateTime }] as const,
  EXPRESSION_TYPES.number,
  (ctx, targetDate) =>
    getDaysPastDueAsOfDateImpl(ctx.getLoanSnapshot().configuration, toLegacyDate(targetDate), ctx.workflowTimeZone.id),
  'Returns the number of days past due for the loan as of the given date.',
);

export const LOAN_FUNCTIONS = {
  [getFunctionSignature(loanHasPaymentDeferral)]: loanHasPaymentDeferral,
  [getFunctionSignature(loanHasRepaymentPlan)]: loanHasRepaymentPlan,
  [getFunctionSignature(loanHasForebearancePlan)]: loanHasForebearancePlan,
  [getFunctionSignature(loanHasNoPastDueStatements)]: loanHasNoPastDueStatements,
  [getFunctionSignature(loanHasQRPCObtainedSince)]: loanHasQRPCObtainedSince,
  [getFunctionSignature(getNextQrpcDueDateTime)]: getNextQrpcDueDateTime,
  [getFunctionSignature(getNumDaysPastDueAsOfDate)]: getNumDaysPastDueAsOfDate,
  [getFunctionSignature(loanHasAppraisalWithId)]: loanHasAppraisalWithId,
  [getFunctionSignature(loanHasInspectionWithId)]: loanHasInspectionWithId,
} satisfies FunctionLibrary;
